import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { ago } from '../live';
import { decimalsFor } from './Garden';

/**
 * One sensor, looked back through.
 *
 * The chart is hand-drawn SVG on the theme tokens rather than a library.
 * Every charting package arrives with its own opinions about colour, type and
 * spacing, and undoing those opinions is more work than drawing a line.
 *
 * Three rules the drawing keeps, because they are the same rules the rest of
 * the product keeps:
 *
 *   Never invent a reading. An uncalibrated probe has raw counts and no
 *   value, so the chart plots counts, says they are counts, and wears the
 *   same italic grey as the live readout. No unit is attached to a number
 *   that has no unit.
 *
 *   A gap is a gap. When the record goes quiet the line stops and starts
 *   again. Joining the two ends would draw a measurement across a stretch
 *   where nothing was measured.
 *
 *   Say what window is on screen. The axis is labelled from the readings
 *   that came back, not from the range that was asked for, and the caption
 *   underneath states the real span in the reader's own timezone. A long
 *   window comes back THINNED - every Nth reading rather than all of them -
 *   and the caption says so, with the count of what is not drawn.
 */

const HOUR = 3600e3;
const DAY = 24 * HOUR;

const RANGES = [
  { key: '24h', label: '24 hours', hours: 24 },
  { key: '7d', label: '7 days', hours: 24 * 7 },
  { key: '30d', label: '30 days', hours: 24 * 30 },
  { key: '90d', label: '90 days', hours: 24 * 90 },
];

// The most points the API will return for one window. Past this it thins -
// every Nth reading, chosen in SQL - rather than cropping to the newest rows,
// so the whole window always comes back and only its resolution drops. Ask for
// the ceiling; anything less just makes a coarser line.
// See app/src/services/api/routers/portal.py, GET /modules/{id}/readings.
const MAX_POINTS = 5000;

const LOCAL_ZONE = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return null; }
})();

const stamp = (t) => new Date(t).toLocaleString([], {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});

/** Axis labels get coarser as the window widens. */
function tickFormatter(span) {
  if (span <= 36 * HOUR) {
    return (t) => new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  if (span <= 10 * DAY) {
    return (t) => new Date(t).toLocaleString([], { weekday: 'short', hour: 'numeric' });
  }
  return (t) => new Date(t).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/** "every 2nd reading", "every 26th". Plain English for a sampling step. */
function ordinal(n) {
  const rest = n % 100;
  if (rest >= 11 && rest <= 13) return `${n}th`;
  return `${n}${{ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th'}`;
}

/** Steps a person would choose: 1, 2, 2.5, 5, 10 and their decades. */
function niceStep(rough) {
  if (!(rough > 0)) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const normalised = rough / magnitude;
  const step = normalised <= 1 ? 1
    : normalised <= 2 ? 2
    : normalised <= 2.5 ? 2.5
    : normalised <= 5 ? 5
    : 10;
  return step * magnitude;
}

function yAxis(min, max, count = 4) {
  if (!(max > min)) {
    // A flat line still deserves somewhere to sit.
    const pad = Math.max(Math.abs(min) * 0.05, 0.5);
    return { ticks: [min], lo: min - pad, hi: min + pad };
  }
  const step = niceStep((max - min) / count);
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const ticks = [];
  for (let i = 0; lo + i * step <= hi + step / 1e6; i += 1) {
    ticks.push(lo + i * step);
  }
  return { ticks, lo, hi };
}

/**
 * How long a silence has to be before it counts as one.
 *
 * Scaled off the probe's own cadence, since a 30 second heartbeat and a five
 * minute one have very different ideas of "quiet". The floor stops a single
 * dropped sample from shattering the line into confetti.
 */
function gapThreshold(points) {
  if (points.length < 4) return Infinity;
  const diffs = [];
  for (let i = 1; i < points.length; i += 1) diffs.push(points[i].t - points[i - 1].t);
  diffs.sort((a, b) => a - b);
  const median = diffs[Math.floor(diffs.length / 2)] || 0;
  return Math.max(median * 5, 2 * 60e3);
}

export default function ModuleHistory({ module, node, onBack }) {
  const [rangeKey, setRangeKey] = useState('24h');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const [hover, setHover] = useState(null);
  const [, setClock] = useState(0);

  const range = RANGES.find((r) => r.key === rangeKey) || RANGES[0];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.readings(module.id, range.hours, MAX_POINTS)
      .then((data) => {
        if (cancelled) return;
        // The window and the sampling travel with the rows, so the axis and
        // the caption can never describe a range the data on screen did not
        // come from, nor claim to draw readings the server thinned away.
        setResult({
          rows: data.readings || [],
          window: data.window || null,
          sampling: data.sampling || null,
          hours: range.hours,
          at: Date.now(),
        });
        setError(null);
      })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [module.id, range.hours, attempt]);

  // Nothing here polls, so "loaded 4 min ago" would freeze at "just now"
  // without a nudge. An age that stops counting is worse than no age.
  useEffect(() => {
    const timer = setInterval(() => setClock((n) => n + 1), 30e3);
    return () => clearInterval(timer);
  }, []);

  const chart = useMemo(() => buildSeries(result), [result]);

  const name = module.name || module.label;
  const unit = chart.mode === 'value' ? module.unit : null;
  const decimals = chart.mode === 'value' ? decimalsFor(module.unit) : 0;

  // A refetch can return fewer readings than the last one, so an index kept
  // from before has to be brought back inside the series.
  const cursor = chart.points.length
    ? Math.min(hover ?? chart.points.length - 1, chart.points.length - 1)
    : null;
  const shown = cursor == null ? null : chart.points[cursor];

  return (
    <div className="stack">
      <button className="btn btn-quiet btn-sm" onClick={onBack} style={{ alignSelf: 'flex-start' }}>
        Back to {node?.name || 'the controller'}
      </button>

      <div className="spread">
        <div>
          <h1>{name}</h1>
          <p className="muted" style={{ margin: 0 }}>
            {module.label}
            {module.role ? ` · ${module.role.replace(/_/g, ' ')}` : ''}
            {' · '}{module.port}
            {node?.name ? ` on ${node.name}` : ''}
          </p>
        </div>
        <button className="btn btn-quiet btn-sm" onClick={() => setAttempt((n) => n + 1)}>
          {loading ? 'Loading…' : result ? `loaded ${ago(result.at)}` : 'Reload'}
        </button>
      </div>

      <div className="range-picker" role="group" aria-label="How far back to look">
        {RANGES.map((r) => (
          <button
            key={r.key}
            type="button"
            className={`btn btn-sm ${r.key === rangeKey ? 'btn-primary' : 'btn-quiet'}`}
            aria-pressed={r.key === rangeKey}
            onClick={() => { setRangeKey(r.key); setHover(null); }}
          >
            {r.label}
          </button>
        ))}
      </div>

      {error && <div className="notice notice-error">{error}</div>}

      {!result && !error && <p className="faint">Fetching the record…</p>}

      {result && chart.points.length === 0 && (
        <Nothing module={module} range={range} />
      )}

      {result && chart.points.length > 0 && (
        <>
          <div className="card chart-card" style={{ opacity: loading ? 0.55 : 1 }}>
            <div className="spread" style={{ alignItems: 'flex-start' }}>
              <div className={`reading ${chart.mode === 'raw' ? 'raw' : ''}`}>
                <span className="label">
                  {hover != null ? 'At the crosshair' : 'Most recent here'}
                </span>
                <span className="value">
                  {chart.mode === 'raw' ? Math.round(shown.v) : shown.v.toFixed(decimals)}
                  {unit && <span className="unit"> {unit}</span>}
                </span>
                <span className="unit">
                  {stamp(shown.t)}
                  {chart.mode === 'raw' ? ' · raw counts, needs calibration' : ''}
                </span>
              </div>

              <Summary chart={chart} unit={unit} decimals={decimals} />
            </div>

            <TimeChart
              chart={chart}
              unit={unit}
              decimals={decimals}
              label={name}
              hover={hover == null ? null : cursor}
              onHover={setHover}
            />
          </div>

          <Caption chart={chart} range={range} />
        </>
      )}
    </div>
  );
}

/**
 * Rows to something drawable.
 *
 * Which number is real depends on the probe. Calibrated modules carry a
 * value; uncalibrated ones carry only the raw count the ADC saw. Whichever
 * one exists is what gets plotted, and the mode travels with it so the
 * drawing and the labels agree.
 */
function buildSeries(result) {
  const empty = {
    mode: 'value', points: [], segments: [], domain: [0, 1],
    thinned: false, stride: 1, total: 0, odd: 0, oddTotal: 0, hours: 24,
  };
  if (!result) return empty;

  const sampling = result.sampling || {};
  const thinned = sampling.thinned === true;
  const stride = sampling.stride || 1;
  const total = sampling.total ?? (result.rows || []).length;
  const oddTotal = sampling.implausible ?? 0;

  const rows = [...(result.rows || [])].sort(
    (a, b) => Date.parse(a.recordedAt) - Date.parse(b.recordedAt));
  if (rows.length === 0) return { ...empty, hours: result.hours };

  const mode = rows.some((r) => r.value !== null && r.value !== undefined) ? 'value' : 'raw';

  const points = [];
  let dropped = false;
  for (const row of rows) {
    const t = Date.parse(row.recordedAt);
    const v = mode === 'value' ? row.value : row.raw;
    if (!Number.isFinite(t) || typeof v !== 'number' || !Number.isFinite(v)) {
      // A row with no usable number is a hole, not a point to draw through.
      dropped = true;
      continue;
    }
    points.push({ t, v, ok: row.plausible !== false, holeBefore: dropped });
    dropped = false;
  }
  if (points.length === 0) return { ...empty, hours: result.hours };

  const threshold = gapThreshold(points);
  const segments = [];
  let run = [points[0]];
  for (let i = 1; i < points.length; i += 1) {
    const quiet = points[i].t - points[i - 1].t > threshold;
    if (quiet || points[i].holeBefore) {
      segments.push(run);
      run = [];
    }
    run.push(points[i]);
  }
  segments.push(run);

  const first = points[0].t;
  const last = points[points.length - 1].t;

  // The axis rules the window that was asked for, because the API now serves
  // all of it: a long range comes back thinned, never cropped to its newest
  // end, so empty stretches inside these bounds are real quiet rather than
  // readings nobody fetched. The server's own bounds are used where it sent
  // them, so the axis and the query cannot drift apart by a round trip.
  const from = Date.parse(result.window?.since ?? '');
  const to = Date.parse(result.window?.until ?? '');
  let domain = Number.isFinite(from) && Number.isFinite(to)
    ? [from, to]
    : [result.at - result.hours * HOUR, result.at];
  if (!(domain[1] > domain[0])) domain = [first - 30 * 60e3, last + 30 * 60e3];

  return {
    mode,
    points,
    segments,
    domain,
    thinned,
    stride,
    total,
    // Out-of-band readings drawn, and out-of-band readings in the window.
    // They differ only when thinning sampled past one, and the caption is
    // owed both numbers rather than the flattering one.
    odd: points.reduce((n, p) => n + (p.ok ? 0 : 1), 0),
    oddTotal,
    hours: result.hours,
    span: last - first,
    first,
    last,
  };
}

function useMeasuredWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    setWidth(element.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      setWidth(entries[0].contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

const HEIGHT = 280;
const PAD = { top: 16, right: 16, bottom: 30, left: 58 };

function TimeChart({ chart, unit, decimals, label, hover, onHover }) {
  const [wrapRef, width] = useMeasuredWidth();
  const svgRef = useRef(null);

  const plotW = Math.max(width - PAD.left - PAD.right, 10);
  const plotH = HEIGHT - PAD.top - PAD.bottom;

  const values = chart.points.map((p) => p.v);
  const { ticks, lo, hi } = yAxis(Math.min(...values), Math.max(...values));
  const [t0, t1] = chart.domain;

  const x = (t) => PAD.left + ((t - t0) / (t1 - t0)) * plotW;
  const y = (v) => PAD.top + ((hi - v) / (hi - lo)) * plotH;

  const format = tickFormatter(t1 - t0);
  const xTickCount = Math.max(2, Math.min(6, Math.floor(plotW / 120)));
  const xTicks = Array.from({ length: xTickCount + 1 },
    (_, i) => t0 + ((t1 - t0) * i) / xTickCount);

  function move(event) {
    const svg = svgRef.current;
    if (!svg || chart.points.length === 0) return;
    const box = svg.getBoundingClientRect();
    const t = t0 + ((event.clientX - box.left - PAD.left) / plotW) * (t1 - t0);
    let best = 0;
    let bestDistance = Infinity;
    for (let i = 0; i < chart.points.length; i += 1) {
      const distance = Math.abs(chart.points[i].t - t);
      if (distance < bestDistance) { bestDistance = distance; best = i; }
    }
    onHover(best);
  }

  const marked = hover != null ? chart.points[hover] : null;
  const readable = `${label} over ${format(t0)} to ${format(t1)}, `
    + `${chart.points.length} readings between `
    + `${Math.min(...values).toFixed(decimals)} and ${Math.max(...values).toFixed(decimals)}`
    + `${unit ? ` ${unit}` : ' raw counts'}.`;

  return (
    <div ref={wrapRef} style={{ minHeight: HEIGHT }}>
      {width > 0 && (
        <svg
          ref={svgRef}
          className={`chart ${chart.mode === 'raw' ? 'raw' : ''}`}
          width={width}
          height={HEIGHT}
          role="img"
          aria-label={readable}
          onPointerMove={move}
          onPointerLeave={() => onHover(null)}
        >
          {ticks.map((v) => (
            <g key={`y${v}`}>
              <line className="grid-line" x1={PAD.left} x2={PAD.left + plotW}
                    y1={y(v)} y2={y(v)} />
              <text className="axis-label" x={PAD.left - 10} y={y(v)}
                    textAnchor="end" dominantBaseline="middle">
                {chart.mode === 'raw' ? Math.round(v) : trimTick(v, decimals)}
              </text>
            </g>
          ))}

          {chart.mode === 'value' && chart.segments.map((run, i) => (
            run.length > 1 && (
              <path key={`a${i}`} className="series-area"
                    d={`${linePath(run, x, y)} L ${x(run[run.length - 1].t)} ${PAD.top + plotH}`
                       + ` L ${x(run[0].t)} ${PAD.top + plotH} Z`} />
            )
          ))}

          {chart.segments.map((run, i) => (
            run.length > 1
              ? <path key={`s${i}`} className="series" d={linePath(run, x, y)} />
              // A reading with silence on both sides is still a reading.
              : <circle key={`s${i}`} className="marker lone"
                        cx={x(run[0].t)} cy={y(run[0].v)} r={2.5} />
          ))}

          {chart.odd > 0 && chart.odd <= 200 && chart.points.map((p, i) => (
            !p.ok && <circle key={`o${i}`} className="odd" cx={x(p.t)} cy={y(p.v)} r={3} />
          ))}

          <line className="axis-line" x1={PAD.left} x2={PAD.left + plotW}
                y1={PAD.top + plotH} y2={PAD.top + plotH} />

          {xTicks.map((t, i) => (
            <text key={`x${t}`} className="axis-label" x={x(t)} y={HEIGHT - 10}
                  textAnchor={i === 0 ? 'start' : i === xTickCount ? 'end' : 'middle'}>
              {format(t)}
            </text>
          ))}

          {marked && (
            <g>
              <line className="crosshair" x1={x(marked.t)} x2={x(marked.t)}
                    y1={PAD.top} y2={PAD.top + plotH} />
              <circle className="marker" cx={x(marked.t)} cy={y(marked.v)} r={4.5} />
            </g>
          )}
        </svg>
      )}
    </div>
  );
}

function linePath(run, x, y) {
  return run.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(p.t).toFixed(1)} ${y(p.v).toFixed(1)}`).join(' ');
}

/** Axis numbers lose trailing zeros; 20.0 reads as false precision. */
function trimTick(v, decimals) {
  const text = v.toFixed(Math.min(decimals + 1, 3));
  return text.replace(/\.?0+$/, '') || '0';
}

function Summary({ chart, unit, decimals }) {
  const values = chart.points.map((p) => p.v);
  const low = Math.min(...values);
  const high = Math.max(...values);
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const show = (v) => (chart.mode === 'raw' ? String(Math.round(v)) : v.toFixed(decimals));

  return (
    <div className="row" style={{ gap: 'var(--s6)', alignItems: 'flex-start' }}>
      {[['Low', low], ['High', high], ['Average', mean]].map(([caption, v]) => (
        <div key={caption} className={`reading small ${chart.mode === 'raw' ? 'raw' : ''}`}>
          <span className="label">{caption}</span>
          <span className="value">
            {show(v)}
            {unit && <span className="unit"> {unit}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

/** What is on screen, said plainly. */
function Caption({ chart, range }) {
  const lines = [];

  lines.push(
    `${chart.points.length.toLocaleString()} `
    + `${chart.points.length === 1 ? 'reading' : 'readings'}, `
    + `${stamp(chart.first)} to ${stamp(chart.last)}`
    + `${LOCAL_ZONE ? `, in your own time (${LOCAL_ZONE})` : ', in your own time'}.`);

  if (chart.mode === 'raw') {
    lines.push('This probe has no calibration, so what you see is the raw count '
      + 'it reports. Counts move with the thing being measured, but they are not '
      + 'a measurement and carry no unit.');
  }

  if (chart.thinned) {
    lines.push(`${range.label} of this probe holds ${chart.total.toLocaleString()} `
      + `readings, more than a chart this wide can show. The line is every `
      + `${ordinal(chart.stride)} one of them. Each point is a reading exactly `
      + 'as it was recorded: nothing here is an average, and the whole '
      + `${range.label} is on the axis. Narrow the window to see every reading.`);
  }

  if (chart.oddTotal > 0 || chart.odd > 0) {
    // Counted over the whole window, not over the sample. A thinned line can
    // step past a short excursion, and a caption that counted only what got
    // drawn would report a healthier probe than the record holds.
    const n = Math.max(chart.oddTotal, chart.odd);
    const missed = chart.thinned && chart.odd < n;
    lines.push(`${n} ${n === 1 ? 'reading is' : 'readings are'} `
      + 'outside the range this probe should be able to report. '
      + (chart.odd > 0
        ? `${missed ? `${chart.odd} of them land on a point this line kept, drawn`
          : 'They are drawn'} as they were recorded, not corrected`
          + `${chart.odd <= 200 ? ', and marked in terracotta' : ''}.`
        : 'None of them landed on a point this thinned line kept.')
      + (missed ? ' Narrow the window to see the rest.' : ''));
  }

  const quiet = chart.segments.length - 1;
  if (quiet > 0) {
    // "Goes quiet" rather than "nothing was recorded" on a thinned line: the
    // breaks are real silences, but their edges are only as sharp as the
    // sampling step, so the stronger claim would be a shade more than is known.
    lines.push(`The line breaks in ${quiet} ${quiet === 1 ? 'place' : 'places'} where `
      + `${chart.thinned ? 'the record goes quiet' : 'nothing was recorded'}. `
      + 'Those stretches are left empty rather than drawn across.');
  }

  return (
    <div className="stack" style={{ gap: 'var(--s2)' }}>
      {lines.map((line) => (
        <p key={line} className="faint" style={{ margin: 0, maxWidth: '68ch' }}>{line}</p>
      ))}
    </div>
  );
}

/** No line to draw. Say why, in a sentence, rather than ruling an empty box. */
function Nothing({ module, range }) {
  const lastHeard = module.latest?.recordedAt || module.lastValueAt;

  return (
    <div className="card empty">
      <h2>{lastHeard ? 'Quiet in this window' : 'Nothing here yet'}</h2>
      {lastHeard ? (
        <p>
          This probe has not reported in the last {range.label}. Its last
          reading on file is from {stamp(lastHeard)}, {ago(lastHeard)}. Try a
          wider window, or check whether the controller is awake.
        </p>
      ) : (
        <p>
          {module.name || module.label} has not sent a reading yet. Once the
          controller is online and this probe starts reporting, every reading
          lands here and you can look back through them.
        </p>
      )}
    </div>
  );
}
